/**
 * Unit 2.6: the client's Polly conversations tab.
 *
 * Beads: insurescope-11zkc.3.6.3 (+ insurescope-m6vie fat-hook split);
 * Unit 6 numbered pagination: insurescope-7149k.2.3.5.
 * Design contracts: docs/design-briefs/2026-06-03-client-conversations.md
 * + the 6.DESIGN brief on insurescope-7149k.2.3.1.
 * A quiet index of the conversations VISIBLE to the viewer for this client —
 * the ones they own plus the ones shared WITH them (collaborator rows carry a
 * "Shared by {name}" chip via ConversationRow). Scoped to the viewer, never
 * org-wide. Scoping is server-side: every request carries client_id, and q
 * is the server's tokenized title and client-name search and composes with
 * client_id; never client-side filtering. The
 * server resolves owned-or-shared visibility (crud_chat_conversation
 * .get_multi_by_user). Numbered pagination via
 * the shared PaginationToolbar, mounted only when total > 10 (below
 * that: no chrome at all — the rows container closes the tab).
 *
 * Thin renderer over `useClientConversationsTab` (the sibling fat-hook
 * pattern; see `useClientListTable`): all query/debounce/branch/paging
 * logic lives in the hook, and this file only maps each branch to its
 * designed state.
 */
import { MessageSquare, PenLine, SearchX } from "lucide-react";

import { PaginationToolbar } from "@/components/shared/PaginationToolbar";
import SearchBar from "@/components/shared/SearchBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { useClientConversationsTab } from "../hooks/useClientConversationsTab";

import { StateShell } from "./ClientListEmptyState";
import { ConversationRow } from "./ConversationRow";

// Re-exported so consumers keep addressing the TAB module (ClientDetail's
// close-time invalidation imports it from here; its test mock re-exports
// the real value through importOriginal, so both seams stay stable).
export { CLIENT_CONVERSATIONS_KEY } from "../hooks/useClientConversationsTab";

export interface IClientConversationsTabProps {
    clientId: number;
    /**
     * The shared page-level Ask Polly handler owned by ClientDetail;
     * it opens the client-scoped ChatSheet (Unit 3.3). Fired by BOTH the
     * toolbar "New conversation" button and the empty-state "Ask Polly"
     * CTA; never a local handler (the brief's single-entry-point rule).
     */
    onAskPolly: () => void;
}

export const ClientConversationsTab = ({
    clientId,
    onAskPolly,
}: IClientConversationsTabProps) => {
    const {
        branch,
        conversations,
        search,
        setSearch,
        q,
        isPlaceholderData,
        refetch,
        showToolbar,
        total,
        page,
        pages,
        pageSize,
        canPreviousPage,
        canNextPage,
        onPageChange,
        onPageSizeChange,
    } = useClientConversationsTab(clientId);

    // Initial load renders the skeleton WITHOUT the toolbar; there is
    // nothing to search yet (every later branch keeps the toolbar).
    if (branch === "loading") {
        return (
            <div className="divide-y rounded-md border">
                {Array.from({ length: 4 }, (_, index) => (
                    <div key={index} className="flex flex-col gap-2 px-4 py-3">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-4 w-full max-w-md" />
                        <Skeleton className="h-3 w-32" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    onClear={() => setSearch("")}
                    placeholder="Search conversations"
                    inputProps={{
                        "aria-label": "Search conversations",
                        maxLength: 200,
                    }}
                    sx={{ maxWidth: 320 }}
                />
                <Button onClick={onAskPolly} className="shrink-0">
                    <PenLine />
                    New conversation
                </Button>
            </div>

            {branch === "error" ? (
                <StateShell
                    className="p-12"
                    icon={
                        <MessageSquare className="text-muted-foreground size-5" />
                    }
                    headline="Something went wrong loading conversations"
                >
                    <Button variant="ghost" onClick={() => refetch()}>
                        Try again
                    </Button>
                </StateShell>
            ) : branch === "empty-search" ? (
                <StateShell
                    data-testid="conversations-empty-state"
                    data-variant="search"
                    className="p-12"
                    icon={<SearchX className="text-muted-foreground size-5" />}
                    headline={`No conversations match “${q}”`}
                >
                    <Button variant="link" onClick={() => setSearch("")}>
                        Clear search
                    </Button>
                </StateShell>
            ) : branch === "empty-library" ? (
                <StateShell
                    data-testid="conversations-empty-state"
                    data-variant="library"
                    className="p-12"
                    icon={
                        <MessageSquare className="text-muted-foreground size-5" />
                    }
                    headline="No conversations yet"
                >
                    <p className="text-muted-foreground max-w-sm text-sm">
                        {
                            "Ask Polly a question about this client's documents to get started."
                        }
                    </p>
                    <Button onClick={onAskPolly}>Ask Polly</Button>
                </StateShell>
            ) : (
                // Rows in a bordered shell with the pagination toolbar as a
                // borderless sibling beneath it — matching the shipped
                // DocumentList placement (the rows own the border; the
                // toolbar sits in plain padding under the list). The dim
                // overlay is scoped to the ROWS region only — the toolbar
                // the user just clicked must never fade mid-interaction.
                <div className="flex flex-col">
                    {/* During a key swap, keepPreviousData shows the OLD
                        rows; dim them and mark the region busy so they
                        read as "refreshing", never as authoritative
                        results (3.6.6 REVIEW, races lane). */}
                    <div
                        data-testid="conversations-list"
                        aria-busy={isPlaceholderData ? true : undefined}
                        className={cn(
                            "divide-y rounded-md border transition-opacity",
                            isPlaceholderData && "opacity-60"
                        )}
                    >
                        {conversations.length === 0
                            ? // A populated branch with an empty slice is
                              // only ever the transient concurrent-shrink
                              // clamp window (insurescope-zp5kp): the
                              // out-of-range probe resolved empty and the
                              // corrected page is in flight. Skeleton the
                              // region — never an empty bordered box.
                              Array.from({ length: 3 }, (_, index) => (
                                  <div
                                      key={index}
                                      className="flex flex-col gap-2 px-4 py-3"
                                  >
                                      <Skeleton className="h-4 w-48" />
                                      <Skeleton className="h-4 w-full max-w-md" />
                                      <Skeleton className="h-3 w-32" />
                                  </div>
                              ))
                            : conversations.map((conversation) => (
                                  <ConversationRow
                                      key={conversation.id}
                                      conversation={conversation}
                                  />
                              ))}
                    </div>
                    {showToolbar && (
                        <div className="py-2">
                            <PaginationToolbar
                                total={total}
                                page={page}
                                pages={pages}
                                pageSize={pageSize}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                onPageChange={onPageChange}
                                onPageSizeChange={onPageSizeChange}
                                entityLabel="conversations"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
